import { showApiError } from "@/lib/toast";
import { uploadContents } from "@/sideEffects/api";
import { selectActiveFirm, selectSelectedFolderGuid } from "@/store/appSelectors";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DropzoneOptions } from "react-dropzone";
import { ContentTypeUI } from "./CreateContentMenu";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { LoadingSmall } from "@/components/LoadingSmall";
import { FileUploadDropzoneForm } from "@/components/upload/FileUploadDropzoneForm";
import { t } from "i18next";
import { format, parse } from "date-fns";
import { Input } from "@/components/ui/input";
import { ScreenSizeSelect } from "../screenSize/ScreenSizeSelect";
import { formatToUTC } from "@/lib/dateUtils";

interface Props {
  onUploadSuccess: () => void;
  onOpenChange: (open: boolean) => void;
  dropzoneOptions: DropzoneOptions;
  typeUI: ContentTypeUI;
}

const formSchema = z
  .object({
    files: z.instanceof(File).array(),
    name: z.string().min(1),
    startDate: z.string().min(1),
    endDate: z.string().min(1),
    screenSizeId: z.number(),
    duration: z.number().min(1),
  })
  .refine(
    (data) => {
      if (!data.startDate || !data.endDate) return true;
      const start = parse(data.startDate, "dd-MM-yyyy HH:mm", new Date());
      const end = parse(data.endDate, "dd-MM-yyyy HH:mm", new Date());
      return end >= start;
    },
    {
      message: t("common.endDateMustBeAfterStartDate"),
      path: ["endDate"],
    }
  );

type FormSchemaType = z.infer<typeof formSchema>;

export const UploadContentSheet: React.FC<Props> = ({ onUploadSuccess, onOpenChange, dropzoneOptions, typeUI }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const selectedFolderGuid = useSelector(selectSelectedFolderGuid);

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      startDate: format(new Date(), "dd-MM-yyyy HH:mm"),
      endDate: "",
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (data: FormSchemaType) => {
      const fileExtension = data.files[0].name.split(".").pop();
      return uploadContents({
        firmGuid: String(activeFirm?.firmGuid),
        isCustom: false,
        files: data.files,
        startDate: formatToUTC(data.startDate),
        endDate: formatToUTC(data.endDate),
        folderGuid: selectedFolderGuid,
        name: `${data.name}.${fileExtension}`,
        screenSizeId: data.screenSizeId,
        duration: data.duration,
      });
    },
    onSuccess: () => {
      onOpenChange(false);
      onUploadSuccess();
    },
    onError: showApiError,
  });

  const onSubmit = (data: FormSchemaType) => {
    uploadMutation.mutate(data);
  };

  return (
    <Sheet open onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t("content.upload", { type: t(`content.${typeUI}`) })}</SheetTitle>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="files"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("fileUpload.dropzone.file")}</FormLabel>
                    <FormControl>
                      <FileUploadDropzoneForm
                        dropzoneOptions={dropzoneOptions}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common.name")}</FormLabel>
                    <FormControl>
                      <div>
                        <Input value={field.value} onChange={field.onChange} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="duration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("contentGroup.duration")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="screenSizeId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("entities.screenSize")}</FormLabel>
                    <FormControl>
                      <ScreenSizeSelect field={field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common.startDate")}</FormLabel>
                    <FormControl>
                      <div>
                        <DatePicker value={field.value} onChange={field.onChange} withTime />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common.endDate")}</FormLabel>
                    <FormControl>
                      <div>
                        <DatePicker value={field.value} onChange={field.onChange} withTime />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={uploadMutation.isPending}>
                {t("content.create")} {uploadMutation.isPending && <LoadingSmall />}
              </Button>
            </form>
          </Form>
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
};
